import React, {
  forwardRef,
  ForwardRefExoticComponent,
  RefAttributes,
  useMemo,
  useState,
} from 'react';

import { Error } from '../error';
import { Field } from '../field';
import type { FieldProps } from '../field/field';
import { Hint } from '../hint';

type Props = RefAttributes<HTMLDivElement> &
  Omit<FieldProps, 'type' | 'icon' | 'onActionIconClick'>;

const COMPONENT_ID = 'modulor-password-field';

export const PasswordField: ForwardRefExoticComponent<Props> = forwardRef<HTMLDivElement, Props>(
  (
    {
      label,
      value,
      leftIcon,
      placeholder,
      error,
      disabled,
      pattern,
      onChange,
      onFocus,
      onBlur,
      onKeyUp,
      onKeyDown,
      maxLength,
      minLength,
      isRequired,
      readOnly,
      clearVisible,
      dataTestId = COMPONENT_ID,
      className,
      autoComplete = 'new-password',
      hint,
      ...rest
    },
    ref,
  ) => {
    const [passwordHidden, setPasswordHidden] = useState(true);
    const togglePasswordVisibility = () => {
      setPasswordHidden((prev) => !prev);
    };

    const icon = useMemo(
      () => (passwordHidden ? 'eye-filled' : 'eye_slash_filled'),
      [passwordHidden],
    );

    return (
      <div
        data-testid={dataTestId}
        data-component-name={COMPONENT_ID}
        data-id={dataTestId}
        ref={ref}
        className={className}
        {...rest}>
        <Field
          autoComplete={autoComplete}
          value={value}
          label={label}
          placeholder={placeholder}
          icon={icon}
          preloadIcon={'eye_slash_filled'}
          onChange={onChange}
          error={!disabled && error}
          disabled={disabled}
          isRequired={isRequired}
          onActionIconClick={togglePasswordVisibility}
          secureText={passwordHidden}
          readOnly={readOnly}
          pattern={pattern}
          minLength={minLength}
          maxLength={maxLength}
          type="password"
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyUp={onKeyUp}
          onKeyDown={onKeyDown}
          clearVisible={clearVisible}
          dataTestId={`${dataTestId}-field`}
          leftIcon={leftIcon}
        />
        {!disabled ? (
          <Error
            error={typeof error === 'string' ? error : null}
            dataTestId={`${dataTestId}-error`}></Error>
        ) : null}
        {hint && !error ? <Hint hint={hint} dataTestId={`${dataTestId}-hint`} /> : null}
      </div>
    );
  },
);

PasswordField.displayName = 'PasswordField';
